import posthog from "posthog-js";

// Public fallback project key, safe to commit. PostHog's project API
// key is the *client-side* token: it's already in every page that loads
// PostHog (that's how the SDK knows where to send events) and PostHog's
// project-level rate limits handle abuse of a leaked key. Inlining this
// guarantees the frontend captures pageviews + UTMs even when the host
// platform (e.g. Lovable Cloud on an external-Supabase project) can't
// inject build-time env vars. If the platform DOES inject
// VITE_POSTHOG_KEY, that value wins.
//
// EU region, picked at signup. If you ever migrate regions, change
// both the key and the host together.
//
// To disable PostHog in a specific environment (e.g. local dev), set
// VITE_POSTHOG_KEY="" (empty string) in .env.local, the nullish-coalesce
// below keeps the empty string, and the early return skips init.
const PUBLIC_FALLBACK_KEY = "phc_kCbfVj2QjfxdhgXfuSSeNG8fp4CzKrFvwjZev6FTtfdC";
const PUBLIC_FALLBACK_HOST = "https://eu.i.posthog.com";

/**
 * Initialise PostHog product analytics for the browser.
 *
 * Called once from main.tsx before React renders. Captures pageviews,
 * UTMs, and clicks via autocapture by default; explicit `posthog.capture(...)`
 * calls layer custom events on top.
 *
 * `person_profiles: 'identified_only'` means we only create person records
 * for users we explicitly identify (via posthog.identify()), anonymous
 * visitors stay anonymous, which keeps our event volume reasonable on
 * the free tier and avoids creating throwaway profiles for one-pager
 * visits from outreach.
 */
export function initPostHog() {
  const key = import.meta.env.VITE_POSTHOG_KEY ?? PUBLIC_FALLBACK_KEY;
  if (!key) return;

  const host = import.meta.env.VITE_POSTHOG_HOST || PUBLIC_FALLBACK_HOST;

  posthog.init(key, {
    api_host: host,
    person_profiles: "identified_only",
    // Capture pageviews automatically on history changes (SPA-aware).
    capture_pageview: true,
    // Don't auto-capture rageclicks/deadclicks, lots of noise on a marketing site.
    capture_dead_clicks: false,
    // Disable in local dev unless explicitly opted in via env var, so
    // founders' debugging sessions don't pollute production analytics.
    loaded: (ph) => {
      if (import.meta.env.DEV && !import.meta.env.VITE_POSTHOG_KEY) {
        ph.opt_out_capturing();
        return;
      }

      // Sprint 67 (2026-05-19): fire the recorder eagerly instead of
      // letting PostHog lazy-load it. Without this, short sessions
      // (bounce visits, quick deep-link hits) fire events with
      // $recording_status = "lazy_loading" and no replay ever gets
      // stored. Paired with project-level
      // session_recording_minimum_duration_milliseconds = 0 to
      // guarantee every captured visit has a replay.
      ph.startSessionRecording();
    },
  });
}

export { posthog };
